import { cn } from "@fieldwork/ui/lib/utils";
import { Link, createFileRoute } from "@tanstack/react-router";

import { Evidence } from "@/components/evidence";
import { Markdown } from "@/components/markdown";
import { NotConnected, useServer } from "@/components/server-hooks";
import { Card, SectionTitle, ServerPage } from "@/components/server-shell";
import { Chip } from "@/components/ui-bits";
import { useAgentQuery } from "@/lib/act";
import { type Evt, KIND, money, type Op, type RunRow, took, when } from "@/lib/runs";

export const Route = createFileRoute("/_auth/servers/$id/runs/$rid")({
  component: RunDetail,
});

type Detail = {
  run: RunRow;
  meta: { cost: number | null; turns: number | null; proposed: number; applied: number; refused: number };
  ops: Op[];
  applied: { op: string; plane_issue: string | null; event_ids: string; payload: string; applied_at: string }[];
  triage: { class: string; project: string; gist: string; thread: { source?: string; account?: string } | null; event_ids: number[] }[];
  guard: { checked: number; refused: { action: string; reason: string }[] };
  docs: Record<string, string>;
  events: Record<string, Evt>;
  people_reverted: boolean;
  prev: number | null;
  next: number | null;
};

const OP_LABEL: Record<string, string> = { create: "New task", update: "Update", comment: "Comment", reprioritize: "Priority", review: "In review", close: "Close" };
/** Triage classes that lead to work read blue; the rest neutral, noise muted. */
const ACTIONABLE = new Set(["asks_me", "task", "decision", "update"]);
const classTone = (c: string) =>
  ACTIONABLE.has(c) ? "bg-[rgba(125,167,240,0.14)] text-[#A7C2F7]" : c === "noise" ? "bg-[#2A2927] text-[#A8A59D]" : "bg-[#2A2927] text-[#CFCBC1]";

const num = (n: number | string) => Number(n).toLocaleString("en-US");
const th = "px-5 py-3 font-medium";

/** "2026-10-17" -> "Fri 17 Oct"; anything else as given. */
function day(s: unknown) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? ""));
  if (!m) return s ? String(s) : "";
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

/** A project name worth showing (not a Plane UUID). */
function projectOf(o: Op) {
  const x = o as Op & { project?: unknown; plane_project?: unknown };
  const v = String(x.project ?? x.plane_project ?? "");
  return v && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v) ? v : "";
}

function parse<T>(s: string, d: T): T {
  try {
    return JSON.parse(s) as T;
  } catch {
    return d;
  }
}

function ClassChip({ c }: { c: string }) {
  return <span className={cn("inline-block rounded-[4px] px-2 py-0.5 font-mono text-[11px]", classTone(c))}>{c.replace(/_/g, " ")}</span>;
}

function RunDetail() {
  const { id, rid } = Route.useParams();
  const connected = !!useServer(id).data?.hub.connected;
  const q = useAgentQuery<Detail>(id, "run_detail", { id: Number(rid) }, connected);
  const back = (
    <Link to="/servers/$id/activity" params={{ id }} className="self-start font-mono text-xs text-[#A8A59D] no-underline">
      ← Activity
    </Link>
  );
  const title = `Run ${/^\d+$/.test(rid) ? num(rid) : rid}`;
  if (!connected)
    return (
      <ServerPage title={title} back={back}>
        <NotConnected />
      </ServerPage>
    );
  if (q.error)
    return (
      <ServerPage title={title} back={back}>
        <p className="text-sm text-red-500">{q.error.message}</p>
      </ServerPage>
    );
  const d = q.data;
  if (!d)
    return (
      <ServerPage title={title} back={back}>
        <p className="m-0 text-sm text-[#A8A59D]">Loading…</p>
      </ServerPage>
    );
  const r = d.run;

  // applied changes (apply mode) come from the database; proposals (suggest mode) from ops.json
  const changes: (Op & { applied?: boolean })[] = d.applied.length
    ? d.applied.map((a) => {
        const src = d.ops.find((o) => o.plane_issue === a.plane_issue && o.op === a.op);
        return {
          ...src,
          op: a.op,
          plane_issue: a.plane_issue,
          event_ids: parse<number[]>(a.event_ids, []),
          payload: parse<Record<string, unknown>>(a.payload, {}),
          reason: (src?.reason as string) ?? "",
          applied: true,
        };
      })
    : d.ops;
  const counts = d.triage.reduce<Record<string, number>>((acc, t) => ({ ...acc, [t.class]: (acc[t.class] ?? 0) + 1 }), {});
  const nav = "inline-flex min-h-9 items-center rounded-[4px] px-3 font-mono text-[13px] no-underline shadow-[inset_0_0_0_1px_#3A3935]";

  return (
    <ServerPage
      back={back}
      title={
        <>
          Run {num(r.id)} <Chip value={r.status} />
        </>
      }
      subtitle={
        <span className="flex flex-wrap gap-x-[18px] gap-y-1.5 font-mono text-xs">
          <span>{KIND[r.mode] ?? r.mode}</span>
          <span>{when(r.started_at)}</span>
          {r.finished_at && <span>took {took(r)}</span>}
          <span>{r.event_count} messages read</span>
          {d.meta.turns ? <span>{d.meta.turns} turns</span> : null}
          {d.meta.cost ? <span>≈ {money(d.meta.cost)}</span> : null}
          {r.model && <span>{r.model}</span>}
        </span>
      }
      actions={
        (d.prev || d.next) && (
          <nav aria-label="Neighbouring runs" className="flex gap-2">
            {d.prev && (
              <Link to="/servers/$id/runs/$rid" params={{ id, rid: String(d.prev) }} className={nav}>
                ← {num(d.prev)}
              </Link>
            )}
            {d.next && (
              <Link to="/servers/$id/runs/$rid" params={{ id, rid: String(d.next) }} className={nav}>
                {num(d.next)} →
              </Link>
            )}
          </nav>
        )
      }
    >
      <div className="flex flex-col gap-7">
        {r.summary && <p className={cn("m-0 max-w-[75ch] text-base", r.status === "failed" && "text-[#F48A80]")}>{r.summary}</p>}
        {d.people_reverted && (
          <p className="m-0 rounded-md bg-[rgba(232,163,61,0.08)] px-[18px] py-3.5 text-sm text-[#F0B65A] shadow-[inset_0_0_0_1px_rgba(232,163,61,0.35)]">
            The people guard reverted this run's changes to the people list (the rejected version is kept on the server).
          </p>
        )}

        <section aria-labelledby="r-ch" className="flex flex-col gap-3">
          <h2 id="r-ch" className="m-0 font-mono text-[15px] font-bold">
            {d.applied.length ? "Plane changes" : "Proposed Plane changes"} <span className="text-[#A8A59D]">{changes.length}</span>
          </h2>
          {changes.length === 0 ? (
            <p className="m-0 text-sm text-[#A8A59D]">No Plane changes in this run.</p>
          ) : (
            <Card className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead>
                  <tr className="text-left font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">
                    <th scope="col" className={`${th} w-[170px]`}>Change</th>
                    <th scope="col" className={`${th} w-[220px]`}>Task</th>
                    <th scope="col" className={th}>Why · evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((o, i) => {
                    const p = o.payload ?? {};
                    const key = o.plane_issue && /^[A-Z][A-Z0-9]*-\d+$/.test(o.plane_issue) ? o.plane_issue : null;
                    const name = String(p.name ?? (o as { title?: unknown }).title ?? "");
                    const meta = [projectOf(o), p.priority && p.priority !== "none" ? String(p.priority) : "", p.target_date ? `due ${day(p.target_date)}` : ""].filter(Boolean);
                    return (
                      <tr key={i} className="border-t border-[#2A2927] align-top">
                        <td className="px-5 py-3.5">
                          <div className="flex flex-wrap gap-1.5">
                            <Chip value="info" label={OP_LABEL[o.op] ?? o.op} />
                            {o.applied ? <Chip value="applied" /> : <Chip value="warn" label="proposed" />}
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          {key && (
                            <Link to="/servers/$id/tasks/$key" params={{ id, key }} className="font-mono">
                              {key}
                            </Link>
                          )}
                          {key && name ? " " : null}
                          {name && <span className={key ? "" : "font-medium"}>{name}</span>}
                          {!key && !name && <span className="text-[#A8A59D]">—</span>}
                          {meta.length > 0 && <div className="text-[13px] text-[#A8A59D]">{meta.join(" · ")}</div>}
                        </td>
                        <td className="flex flex-col gap-2 px-5 py-3.5">
                          {o.reason && <span>{o.reason}</span>}
                          <Evidence ids={o.event_ids ?? []} events={d.events} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </section>

        {d.triage.length > 0 && (
          <section aria-labelledby="r-tri" className="flex flex-col gap-3">
            <h2 id="r-tri" className="m-0 font-mono text-[15px] font-bold">
              How it sorted the {d.triage.length} {d.triage.length === 1 ? "thread" : "threads"}
            </h2>
            <div className="flex flex-wrap gap-2">
              {Object.entries(counts).map(([c, n]) => (
                <span key={c} className={cn("rounded-[4px] px-2.5 py-[5px] font-mono text-xs", classTone(c))}>
                  {c.replace(/_/g, " ")} {n}
                </span>
              ))}
            </div>
            <Card className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead>
                  <tr className="text-left font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">
                    <th scope="col" className={`${th} w-[150px]`}>Class</th>
                    <th scope="col" className={`${th} w-[140px]`}>Project</th>
                    <th scope="col" className={th}>Thread</th>
                  </tr>
                </thead>
                <tbody>
                  {d.triage.map((t, i) => {
                    const noise = t.class === "noise";
                    const where = [t.thread?.source, t.thread?.account].filter(Boolean).join(" ");
                    return (
                      <tr key={i} className="border-t border-[#2A2927] align-top">
                        <td className="px-5 py-3">
                          <ClassChip c={t.class ?? "?"} />
                        </td>
                        <td className={cn("px-5 py-3", t.project ? "font-mono text-[13px]" : "text-[#A8A59D]")}>{t.project || "—"}</td>
                        <td className="flex flex-col gap-1.5 px-5 py-3">
                          <span className={noise ? "text-[#A8A59D]" : ""}>
                            {t.gist}
                            {where && <span className="text-[#A8A59D]"> · {where}</span>}
                          </span>
                          <Evidence ids={t.event_ids} events={d.events} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </section>
        )}

        <section aria-labelledby="r-guard" className="flex flex-col gap-3">
          <h2 id="r-guard" className="m-0 font-mono text-[15px] font-bold">
            Guard
          </h2>
          <p className="m-0 text-[#A8A59D]">
            Every Plane call is checked against your rules before it’s made. {d.guard.checked} {d.guard.checked === 1 ? "call" : "calls"} checked ·{" "}
            {d.guard.refused.length} refused.
          </p>
          {d.guard.refused.map((g, i) => (
            <div key={i} className="flex flex-col gap-1 rounded-md bg-[rgba(232,163,61,0.08)] px-[18px] py-3.5 shadow-[inset_0_0_0_1px_rgba(232,163,61,0.35)]">
              <span className="font-mono text-[13px] text-[#F0B65A]">{g.action} · refused</span>
              <span className="text-[#CFCBC1]">{g.reason}</span>
            </div>
          ))}
        </section>

        {Object.entries(d.docs).map(([name, text]) => (
          <section key={name} className="flex flex-col gap-3">
            <SectionTitle>{name === "digest.md" ? "Run notes" : name.replace(".md", "")}</SectionTitle>
            <Card className="max-w-[75ch] px-[22px] py-[18px]">
              <Markdown text={text} />
            </Card>
          </section>
        ))}
      </div>
    </ServerPage>
  );
}
